import type { StaffFulfilmentViewResponse } from "@lafayette/schemas";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Fact, Facts } from "../../ui/facts";
import { Icon } from "../../ui/icon";
import { Notice } from "../../ui/notice";
import { Panel } from "../../ui/panel";
import { StaffOnlyNote } from "../../ui/staff-only-note";
import type { Availability } from "../availability";
import { CustomerReadsNote, emptyNoteClass, hintClass, listClass } from "../components/parts";
import { formatBytes, formatInstant } from "../format";
import {
  attachmentTypeLabels,
  confirmationKindLabels,
  contactMethodLabels,
  fulfilmentKindLabels,
  updateKindLabels,
} from "../labels";
import { formatInstantInZone } from "../wall-time";

// The staff fulfilment sections (CHT-752): the evidence staff recorded, the updates published for the customer and the attachments. The
// evidence is STAFF ONLY and says so. The updates are what the customer reads, word for word, and say so. An attachment is staff only
// until staff share it on purpose. Times are shown in UTC and, when the request has a zone, in that zone too, always with the zone name.

const dash = "—";

type Evidence = StaffFulfilmentViewResponse["evidence"][number];
type Update = StaffFulfilmentViewResponse["customerUpdates"][number];
type Attachment = StaffFulfilmentViewResponse["attachments"][number];

const actorName = (actors: Readonly<Record<string, string>>, id: string | null): string =>
  id === null ? dash : (actors[id] ?? "a staff member");

function when(value: string | null, zone: string | null): string {
  if (value === null) return dash;
  const local = zone === null ? "" : ` (${formatInstantInZone(value, zone)})`;
  return `${formatInstant(value)}${local}`;
}

/** A control that is drawn disabled says why in words next to it. */
function ActionButton({
  id,
  label,
  availability,
  onClick,
  secondary = true,
  subject,
}: Readonly<{
  id: string;
  label: string;
  availability: Availability | null;
  onClick: () => void;
  secondary?: boolean;
  /** What the control acts on, read by a screen reader after the label, so two controls in a list differ (WALK-3). Text, not an attribute. */
  subject?: string;
}>) {
  if (availability === null) return null;
  return (
    <span className="flex max-w-64 flex-col items-start gap-1">
      <Button
        variant={secondary ? "outline" : "default"}
        disabled={!availability.available}
        aria-describedby={availability.available ? undefined : `${id}-reason`}
        onClick={onClick}
      >
        {label}
        {subject === undefined ? null : (
          <>
            {" "}
            <span className="sr-only">{subject}</span>
          </>
        )}
      </Button>
      {availability.available ? null : (
        <span id={`${id}-reason`} className={hintClass}>
          {availability.reason}
        </span>
      )}
    </span>
  );
}

const rowBox = "grid gap-2 rounded-md border border-border bg-surface-sunken p-3 [&_p]:m-0";

// --- evidence ---------------------------------------------------------------------------------------------------------------------

export function EvidenceSection({
  evidence,
  truncated,
  actors,
  zone,
  recordAction,
  onRecord,
  onCorrect,
  canCorrectRow,
}: Readonly<{
  evidence: readonly Evidence[];
  truncated: boolean;
  actors: Readonly<Record<string, string>>;
  zone: string | null;
  recordAction: Availability | null;
  onRecord: () => void;
  onCorrect: (row: Evidence) => void;
  canCorrectRow: (row: Evidence) => boolean;
}>) {
  return (
    <Panel id="request-evidence-title" title="Fulfilment evidence">
      <StaffOnlyNote>
        What suppliers said, quotes and confirmation references. The customer reads only the updates
        you publish.
      </StaffOnlyNote>
      <ActionButton
        id="request-record-evidence"
        label="Record evidence"
        availability={recordAction}
        onClick={onRecord}
      />
      {evidence.length === 0 ? (
        <p className={emptyNoteClass}>
          No evidence recorded yet. A request can only be confirmed on a confirmation reference.
        </p>
      ) : (
        <ol className={listClass}>
          {evidence.map((row) => (
            <li
              key={row.id}
              className={`${rowBox}${row.supersededByEvidenceId === null ? "" : " line-through opacity-75"}${row.canConfirm ? " border-2 border-success" : ""}`}
            >
              <p className="flex flex-wrap items-center gap-1.5">
                <strong>{fulfilmentKindLabels[row.kind] ?? row.kind}</strong>
                {row.isConfirmation ? (
                  <Badge tone="success">
                    <Icon name="checkmark-square-02" />
                    Confirms this request
                  </Badge>
                ) : null}
                {row.canConfirm && !row.isConfirmation ? (
                  <Badge tone="success">
                    <Icon name="target-02" />
                    Can confirm now
                  </Badge>
                ) : null}
                {row.supersededByEvidenceId === null ? null : (
                  <Badge tone="warning">
                    <Icon name="file-edit" />
                    Corrected by a later row
                  </Badge>
                )}
                {row.supersedesEvidenceId === null ? null : <Badge>Corrects an earlier row</Badge>}
              </p>
              <Facts>
                <Fact term="Recorded">{when(row.observedAt, zone)}</Fact>
                {row.sourceTimestamp === null ? null : (
                  <Fact term="The supplier said it">{when(row.sourceTimestamp, zone)}</Fact>
                )}
                {row.supplierLabel === null ? null : (
                  <Fact term="Supplier">{row.supplierLabel}</Fact>
                )}
                {row.contactMethod === null ? null : (
                  <Fact term="Contact">
                    {contactMethodLabels[row.contactMethod] ?? row.contactMethod}
                  </Fact>
                )}
                {row.referenceCode === null ? null : (
                  <Fact term="Reference">
                    {row.referenceCode}
                    {row.confirmationKind === null
                      ? ""
                      : ` (${confirmationKindLabels[row.confirmationKind] ?? row.confirmationKind})`}
                  </Fact>
                )}
                {row.expiresAt === null ? null : (
                  <Fact term="Expires">{when(row.expiresAt, zone)}</Fact>
                )}
                {row.paymentQuoteId === null ? null : (
                  <Fact term="Payment quote">
                    {row.paymentQuoteId} (the amount lives in the approved quote, not here)
                  </Fact>
                )}
                {row.detail === null ? null : <Fact term="Detail">{row.detail}</Fact>}
                <Fact term="Recorded by">{actorName(actors, row.createdByStaffUserId)}</Fact>
              </Facts>
              {canCorrectRow(row) ? (
                <div>
                  <Button variant="outline" onClick={() => onCorrect(row)}>
                    Correct this row
                    <span className="sr-only">
                      {" "}
                      ({fulfilmentKindLabels[row.kind] ?? row.kind}, recorded{" "}
                      {formatInstant(row.observedAt)})
                    </span>
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ol>
      )}
      {truncated ? (
        <Notice tone="warning" role="status">
          <p>Older evidence exists that this list does not show.</p>
        </Notice>
      ) : null}
    </Panel>
  );
}

// --- customer updates ---------------------------------------------------------------------------------------------------------------

export function CustomerUpdatesSection({
  updates,
  truncated,
  actors,
  publishAction,
  onPublish,
}: Readonly<{
  updates: readonly Update[];
  truncated: boolean;
  actors: Readonly<Record<string, string>>;
  publishAction: Availability | null;
  onPublish: () => void;
}>) {
  return (
    <Panel id="request-updates-title" title="Updates for the customer">
      <CustomerReadsNote>
        <strong>The customer reads these updates exactly as written.</strong> Publishing is the
        approval. Nothing here is edited or deleted afterwards.
      </CustomerReadsNote>
      <ActionButton
        id="request-publish-update"
        label="Publish a customer update"
        availability={publishAction}
        onClick={onPublish}
      />
      {updates.length === 0 ? (
        <p className={emptyNoteClass}>No update has been published.</p>
      ) : (
        <ol className={listClass}>
          {updates.map((update) => (
            <li key={update.id} className={rowBox}>
              <p className="flex flex-wrap items-center gap-1.5">
                <strong>{updateKindLabels[update.kind] ?? update.kind}</strong>
                {update.isCorrection ? (
                  <Badge tone="warning">
                    <Icon name="file-edit" />
                    Corrected
                  </Badge>
                ) : null}
              </p>
              <p className="break-words">{update.text}</p>
              <p className={hintClass}>
                Published {formatInstant(update.createdAt)} by{" "}
                {actorName(actors, update.createdByStaffUserId)}
                {update.evidenceId === null
                  ? ""
                  : ". It rests on a recorded evidence row (staff only)."}
              </p>
            </li>
          ))}
        </ol>
      )}
      {truncated ? (
        <Notice tone="warning" role="status">
          <p>Older updates exist that this list does not show.</p>
        </Notice>
      ) : null}
    </Panel>
  );
}

// --- attachments --------------------------------------------------------------------------------------------------------------------

export function AttachmentsSection({
  attachments,
  truncated,
  actors,
  addAction,
  shareAction,
  onAdd,
  onShare,
  onDownload,
  downloadingId,
  downloadMessage,
}: Readonly<{
  attachments: readonly Attachment[];
  truncated: boolean;
  actors: Readonly<Record<string, string>>;
  addAction: Availability | null;
  /** Sharing is judged by who may work the request; it needs no particular state. */
  shareAction: Availability | null;
  onAdd: () => void;
  onShare: (attachment: Attachment) => void;
  /** CHT-754: asks for a signed link and opens the file in a new tab. Staff only: it needs `request.read_internal`. */
  onDownload: (attachment: Attachment) => void;
  /** The attachment whose link is being made, or `null`. */
  downloadingId: string | null;
  /** The plain-words result of the last failed download, or `null`. */
  downloadMessage: string | null;
}>) {
  return (
    <Panel id="request-attachments-title" title="Attachments">
      <StaffOnlyNote>
        An attachment is staff only until you share it with the customer. Sharing cannot be undone.
      </StaffOnlyNote>
      <p className={hintClass}>
        Upload a PDF, PNG, JPEG or WebP file of up to 10 MiB, with at most 20 files on a request.
        Only staff can open a file until you share it.
      </p>
      <ActionButton
        id="request-add-attachment"
        label="Upload an attachment"
        availability={addAction}
        onClick={onAdd}
      />
      {attachments.length === 0 ? (
        <p className={emptyNoteClass}>No attachment uploaded.</p>
      ) : (
        <ul className={listClass}>
          {attachments.map((attachment) => (
            <li key={attachment.id} className={rowBox}>
              <p className="flex flex-wrap items-center gap-1.5">
                <strong className="break-all">{attachment.displayName}</strong>
                {attachment.visibility === "SHARED" ? (
                  <Badge tone="info">
                    <Icon name="view" />
                    Shared with the customer
                  </Badge>
                ) : (
                  <Badge>
                    <Icon name="square-lock-02" />
                    Staff only
                  </Badge>
                )}
              </p>
              <Facts>
                <Fact term="Type and size">
                  {attachmentTypeLabels[attachment.contentType] ?? attachment.contentType},{" "}
                  {formatBytes(attachment.sizeBytes)}
                </Fact>
                <Fact term="Added">
                  {formatInstant(attachment.createdAt)} by{" "}
                  {actorName(actors, attachment.createdByStaffUserId)}
                </Fact>
                {attachment.sharedAt === null ? null : (
                  <Fact term="Shared">
                    {formatInstant(attachment.sharedAt)} by{" "}
                    {actorName(actors, attachment.sharedByStaffUserId)}
                  </Fact>
                )}
              </Facts>
              <div className="flex flex-wrap items-start gap-2">
                <Button
                  id={`request-download-${attachment.id}`}
                  variant="outline"
                  disabled={downloadingId !== null}
                  onClick={() => onDownload(attachment)}
                >
                  {downloadingId === attachment.id ? "Opening…" : "Download"}
                  <span className="sr-only"> {attachment.displayName}</span>
                </Button>
                {attachment.visibility === "STAFF_ONLY" ? (
                  <ActionButton
                    id={`request-share-${attachment.id}`}
                    label="Share with the customer"
                    availability={shareAction}
                    subject={attachment.displayName}
                    onClick={() => onShare(attachment)}
                  />
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      {downloadMessage === null ? null : (
        <Notice tone="warning" role="alert">
          <p>{downloadMessage}</p>
        </Notice>
      )}
      {truncated ? (
        <Notice tone="warning" role="status">
          <p>Older attachments exist that this list does not show.</p>
        </Notice>
      ) : null}
    </Panel>
  );
}
